import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';
import { Toaster } from 'react-hot-toast';
import { useSimplifiedAuth } from '../hooks/useSimplifiedAuth';
import OneClickCheckout from './auth/OneClickCheckout';
import CosmicLoadingScreen from './ui/CosmicLoadingScreen';
import { store } from '../redux/store';

// Lazy load pages
const LandingPage = lazy(() => import('../pages/LandingPage'));
const Dashboard = lazy(() => import('../pages/Dashboard'));
const BlogListPage = lazy(() => import('../pages/BlogListPage'));
const BlogPostPage = lazy(() => import('../pages/BlogPostPage'));
const NewsletterPage = lazy(() => import('../pages/NewsletterPage'));
const PricingPage = lazy(() => import('../pages/PricingPage'));
const Satellites = lazy(() => import('../pages/Satellites'));
const Photos = lazy(() => import('../pages/Photos'));
const Preferences = lazy(() => import('../pages/Preferences'));
const PaymentSuccessPage = lazy(() => import('../pages/PaymentSuccessPage'));
const PaymentErrorHandler = lazy(() => import('../components/PaymentErrorHandler'));

// Simple route guard component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { authState, showOneClickCheckout } = useSimplifiedAuth();
  
  if (authState === 'loading') {
    return <CosmicLoadingScreen message="Connecting to the cosmos..." />;
  }
  
  if (authState === 'unauthenticated') {
    // Show checkout modal instead of redirecting
    showOneClickCheckout();
    // Show landing page as fallback
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

// Inner component that uses auth hooks (inside Router context)
const AppContent: React.FC = () => {
  const { authState, userFlow } = useSimplifiedAuth();
  const [showCheckout, setShowCheckout] = useState(false);

  // Listen for checkout modal requests
  useEffect(() => {
    const handleShowCheckout = () => {
      setShowCheckout(true);
    };

    window.addEventListener('showOneClickCheckout', handleShowCheckout);
    return () => window.removeEventListener('showOneClickCheckout', handleShowCheckout);
  }, []);

  // Show loading screen during initial auth check
  if (authState === 'loading') {
    return <CosmicLoadingScreen message="Initializing SpacePings..." />;
  }

  return (
    <div className="App">
          <Suspense fallback={<CosmicLoadingScreen message="Loading..." />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/pricing" element={<PricingPage />} />
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
              <Route path="/newsletter/:slug" element={<NewsletterPage />} />
              <Route path="/payment-success" element={<PaymentSuccessPage />} />
              <Route path="/payment-error" element={<PaymentErrorHandler />} />

              {/* Protected Routes */}
              <Route 
                path="/dashboard" 
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/satellites" 
                element={
                  <ProtectedRoute>
                    <Satellites />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/photos" 
                element={
                  <ProtectedRoute>
                    <Photos />
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/preferences" 
                element={
                  <ProtectedRoute>
                    <Preferences />
                  </ProtectedRoute>
                } 
              />

              {/* Redirect auth page to dashboard if authenticated, otherwise show landing */}
              <Route 
                path="/auth" 
                element={
                  authState === 'authenticated' 
                    ? <Navigate to="/dashboard" replace />
                    : <Navigate to="/" replace />
                } 
              />

              {/* Catch all - redirect to landing */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>

          {/* One-Click Checkout Modal */}
          <OneClickCheckout
            isVisible={showCheckout}
            onClose={() => setShowCheckout(false)}
            onSuccess={() => {
              setShowCheckout(false);
              // Auto-redirect to dashboard after successful payment
              window.location.href = '/dashboard';
            }}
          />

          {/* Analytics and Notifications */}
          <Analytics />
          <SpeedInsights />
          <Toaster 
            position="bottom-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#1e293b',
                color: '#ffffff',
                border: '1px solid rgba(139, 92, 246, 0.3)',
              },
            }}
          />
        </div>
  );
};

// Main app component that establishes Router context first
const SimplifiedApp: React.FC = () => {
  return (
    <Provider store={store}>
      <HelmetProvider>
        <Router>
          <AppContent />
        </Router>
      </HelmetProvider>
    </Provider>
  );
};

export default SimplifiedApp;